import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import {
  useNavigation,
  useRoute,
  RouteProp,
} from '@react-navigation/native';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import Toast from 'react-native-toast-message';
import { BrandLogo } from '../../components/BrandLogo';
import { fpColors } from '../../styles/theme';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { selectUser } from '../auth/authSlice';
import {
  archiveGroup,
  cancelGroupInviteByEmail,
  inviteGroupMember,
  loadGroupDetail,
  patchGroup,
  patchGroupMemberRole,
  removeGroupMember,
  selectGroupDetail,
  selectGroupsLoading,
  selectGroupsError,
  clearGroupsError,
} from './groupsSlice';
import type { GroupMemberRole } from '../../api/types';
import { MemberRow } from './components/MemberRow';
import {
  InviteCooldownModal,
  parseInviteCooldown,
} from './components/InviteCooldownModal';
import { GroupInventorySection } from './components/GroupInventorySection';
import { GroupActivityTab } from './components/GroupActivityTab';
import type { GroupKind, GroupMember } from '../../api/types';

/**
 * GroupDetailScreen — Sprint 1 (Chunk 8) replacement for the
 * transitional ManageGroupMembersScreen + GroupPermissionsScreen pair.
 *
 * Single screen with two stacked sections:
 *   - Members (invite form for OWNER/ADMIN + roster)
 *   - Settings (auto-approve toggle + rename + archive — OWNER only)
 *
 * Tabbed scaffold deferred to Chunk 9 when there's a second populated
 * tab (Inventory). Sprint 1 ships flat sections — cleaner with the
 * data we have and avoids placeholder-tab churn.
 *
 * BDD scenarios + ASCII wireframes documented on issue #32.
 */

type GroupDetailRouteParams = {
  GroupDetail: { groupId: string };
};

const KIND_LABEL: Record<GroupKind, string> = {
  CREW: 'CIRCLE',
  FAMILY: 'FAMILY',
  TEAM: 'TEAM',
  FRIENDS: 'FRIENDS',
  NEIGHBOURS: 'NEIGHBOURS',
  PERSONAL: 'PERSONAL',
};

export const GroupDetailScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<RouteProp<GroupDetailRouteParams, 'GroupDetail'>>();
  const dispatch = useAppDispatch();

  const groupId = useMemo(() => {
    const raw = route.params?.groupId;
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? num : null;
  }, [route.params]);

  const detail = useAppSelector(selectGroupDetail(groupId));
  const loading = useAppSelector(selectGroupsLoading);
  const error = useAppSelector(selectGroupsError);
  const currentUser = useAppSelector(selectUser);

  // ── Local form state ────────────────────────────────────────────
  const [inviteEmail, setInviteEmail] = useState('');
  // Search-UX §3.6 — local member-filter query.  Matches members
  // case-insensitively against name + email (covers both registered
  // members and pending email-only invitees).  Local state only —
  // no Redux, no server call.
  const [memberFilter, setMemberFilter] = useState('');
  const [editedName, setEditedName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [archiving, setArchiving] = useState(false);
  // FE-232 — Invite cooldown modal state. Populated when the server
  // returns 429 with code=invite_cooldown on either the initial invite
  // form OR the per-row resend button.
  const [cooldown, setCooldown] = useState<{
    visible: boolean;
    email: string | null;
    expiresAt: string | null;
    declineCount: number | null;
  }>({
    visible: false,
    email: null,
    expiresAt: null,
    declineCount: null,
  });

  useEffect(() => {
    if (groupId != null) {
      dispatch(clearGroupsError());
      dispatch(loadGroupDetail(groupId));
    }
  }, [dispatch, groupId]);

  // FE-255 PR5 — refetch group detail (inventory + members + invites)
  // when the user comes back to this screen. Group state changes
  // through many side channels (members invited, items added by
  // other crew members, custody flipped) and a stale roster confuses
  // every Manage action.
  const refetchGroupDetail = useCallback(() => {
    if (groupId != null) dispatch(loadGroupDetail(groupId));
  }, [dispatch, groupId]);
  useRefetchOnFocus(refetchGroupDetail);

  useEffect(() => {
    if (detail) setEditedName(detail.name);
  }, [detail?.id, detail?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Role derivation (backend doesn't yet return my_role on detail; #103) ─
  const myRole = useMemo(() => {
    if (!detail) return undefined;
    if (detail.my_role) return detail.my_role;
    if (currentUser?.id == null) return undefined;
    if (detail.owner_user_id === currentUser.id) return 'OWNER' as const;
    const me = detail.members.find((m) => m.user_id === currentUser.id);
    return me?.role;
  }, [detail, currentUser?.id]);

  const isOwner = myRole === 'OWNER';
  const isAdmin = isOwner || myRole === 'ADMIN';

  // ── Bad route param: groupId missing or synthetic Personal (-1) ──
  if (groupId == null) {
    return (
      <View style={styles.page}>
        <Header onBack={() => goBackOrHome(navigation)} title="Not available" />
        <View style={styles.errorWrap}>
          <MaterialIcons name="error-outline" size={32} color={fpColors.outline} />
          <Text style={styles.errorTitle}>Circle not found</Text>
          <Text style={styles.errorMeta}>
            That link or tile didn't resolve to a real circle.
          </Text>
          <Pressable
            onPress={() => goBackOrHome(navigation)}
            style={({ pressed }) => [
              styles.primaryCta,
              pressed && { opacity: 0.88 },
            ]}
            accessibilityRole="button"
          >
            <Text style={styles.primaryCtaText}>Back to circles</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  // ── Handlers ────────────────────────────────────────────────────

  const submitInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email.includes('@') || !email.includes('.')) {
      Toast.show({ type: 'error', text1: 'Enter a valid email' });
      return;
    }
    // PR31 — pre-check the local roster to avoid creating dupes.
    // Backend currently doesn't gate this (BE-212 will add 409 with
    // MEMBER_ALREADY_INVITED) so the FE has to be the source of
    // truth until then.
    const trimmed = email.trim().toLowerCase();
    const existing = detail?.members.find((m) => {
      const memberEmail = (m.email || m.invitee_email || '').toLowerCase();
      return memberEmail === trimmed;
    });
    if (existing) {
      if (existing.status === 'ACTIVE') {
        Toast.show({
          type: 'info',
          text1: `${email} is already in this circle`,
        });
        return;
      }
      if (existing.status === 'INVITED') {
        Toast.show({
          type: 'info',
          text1: 'Invite already pending',
          text2:
            'They haven\'t accepted or declined yet. Re-sending the email is on the roadmap.',
        });
        return;
      }
    }
    try {
      await dispatch(
        inviteGroupMember({ groupId, request: { invitee_email: email } }),
      ).unwrap();
      setInviteEmail('');
      Toast.show({
        type: 'success',
        text1: `Invite sent`,
        text2: email,
      });
      // Refresh detail so the new INVITED row shows up.
      dispatch(loadGroupDetail(groupId));
    } catch (err: any) {
      // FE-232 — 429 invite_cooldown takes over the error surface
      // with a live countdown instead of a fire-and-forget toast.
      // Other structured errors fall through to the generic toast.
      const errData = (err && typeof err === 'object' ? err : {}) as any;
      if (errData.code === 'invite_cooldown') {
        const parsed = parseInviteCooldown({
          response: { data: { code: errData.code, context: errData.context } },
        });
        setCooldown({
          visible: true,
          email,
          expiresAt: parsed?.expiresAt ?? null,
          declineCount: parsed?.declineCount ?? null,
        });
        return;
      }
      Toast.show({
        type: 'error',
        text1: 'Could not send invite',
        text2: typeof err === 'string' ? err : 'Please try again.',
      });
    }
  };

  const handleRemove = (member: GroupMember) => {
    const display =
      member.name || member.email || member.invitee_email || 'this member';

    // FE-216 (PR32 supersedes PR31's stub) — pending INVITED rows are
    // now cancellable via cancelGroupInviteByEmail (BE-212 shipped),
    // wired through MemberRow's onCancelInvite. handleRemove now ONLY
    // services ACTIVE rows. If somehow a parent dispatch arrives for a
    // user-less row we no-op silently — the new path covers it.
    if (member.user_id == null) {
      return;
    }

    const targetUserId = member.user_id;
    const title = 'Remove from circle?';
    const message = `${display} will lose access to this circle.`;
    const run = async () => {
      try {
        await dispatch(
          removeGroupMember({ groupId, userId: targetUserId }),
        ).unwrap();
        Toast.show({ type: 'success', text1: 'Member removed' });
      } catch (err: any) {
        Toast.show({
          type: 'error',
          text1: 'Could not remove',
          text2: typeof err === 'string' ? err : 'Please try again.',
        });
        dispatch(loadGroupDetail(groupId));
      }
    };
    // FE#242 — RN Alert.alert is a no-op on react-native-web, so member
    // removal silently did nothing on web. Branch to window.confirm (same
    // pattern as handleArchive); keep Alert.alert for native.
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      if (window.confirm(`${title}\n\n${message}`)) run();
    } else {
      Alert.alert(title, message, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Remove', style: 'destructive', onPress: () => run() },
      ]);
    }
  };

  // FE-216 — cancel pending invite by email (no confirm — single-tap UX)
  const handleCancelInvite = async (gid: number, email: string) => {
    try {
      await dispatch(
        cancelGroupInviteByEmail({ groupId: gid, email }),
      ).unwrap();
      Toast.show({
        type: 'success',
        text1: 'Invite cancelled',
        text2: email,
      });
    } catch (err: any) {
      Toast.show({
        type: 'error',
        text1: 'Could not cancel invite',
        text2: typeof err === 'string' ? err : 'Please try again.',
      });
      dispatch(loadGroupDetail(gid));
    }
  };

  // FE-216 — resend invite (BE rotates token + re-emails). Reuses the
  // existing inviteGroupMember thunk — server treats the second POST
  // with the same email as a refresh (no 409 because the existing row
  // is INVITED, not ACTIVE).
  const handleResendInvite = async (gid: number, email: string) => {
    try {
      await dispatch(
        inviteGroupMember({ groupId: gid, request: { invitee_email: email } }),
      ).unwrap();
      Toast.show({
        type: 'success',
        text1: 'Invite resent',
        text2: email,
      });
      dispatch(loadGroupDetail(gid));
    } catch (err: any) {
      // FE-232 — 429 invite_cooldown → live countdown modal.
      const errData = (err && typeof err === 'object' ? err : {}) as any;
      if (errData.code === 'invite_cooldown') {
        const parsed = parseInviteCooldown({
          response: { data: { code: errData.code, context: errData.context } },
        });
        setCooldown({
          visible: true,
          email,
          expiresAt: parsed?.expiresAt ?? null,
          declineCount: parsed?.declineCount ?? null,
        });
        return;
      }
      Toast.show({
        type: 'error',
        text1: 'Could not resend',
        text2: typeof err === 'string' ? err : 'Please try again.',
      });
    }
  };

  // FE-217 — promote / demote / transfer ownership. Atomic OWNER swap
  // happens server-side per SD-7: promoting another user to OWNER also
  // demotes the caller (previous OWNER) to ADMIN in one transaction.
  const handleChangeRole = async (
    gid: number,
    userId: number,
    next: GroupMemberRole,
  ) => {
    // Confirm before destructive ownership transfer (per § D.2 wireframe).
    if (next === 'OWNER') {
      const title = 'Transfer ownership?';
      const message = `They'll become the owner of this circle. You'll become an admin.\n\nYou can still invite, manage members and items — but only the new owner can transfer ownership again or delete the circle.`;
      // FE#242 — RN Alert.alert is a no-op on web; the awaited Promise would
      // never resolve and the transfer would hang. Branch to window.confirm.
      let confirmed: boolean;
      if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
        confirmed = window.confirm(`${title}\n\n${message}`);
      } else {
        confirmed = await new Promise<boolean>((resolve) => {
          Alert.alert(title, message, [
            { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
            { text: 'Transfer', onPress: () => resolve(true) },
          ]);
        });
      }
      if (!confirmed) return;
    }

    try {
      await dispatch(
        patchGroupMemberRole({ groupId: gid, userId, role: next }),
      ).unwrap();
      Toast.show({
        type: 'success',
        text1:
          next === 'OWNER'
            ? 'Ownership transferred'
            : next === 'ADMIN'
              ? 'Promoted to admin'
              : 'Demoted to member',
      });
      // Refresh detail so the caller's own role flip (when OWNER swap
      // demotes them) and the member row update both render.
      dispatch(loadGroupDetail(gid));
    } catch (err: any) {
      Toast.show({
        type: 'error',
        text1: 'Could not change role',
        text2: typeof err === 'string' ? err : 'Please try again.',
      });
    }
  };

  const handleToggleAutoApprove = async (next: boolean) => {
    if (!isOwner || !detail) return;
    try {
      await dispatch(
        patchGroup({ groupId, patch: { auto_approve_handoff: next } }),
      ).unwrap();
      Toast.show({
        type: 'success',
        text1: next ? 'Auto-approve enabled' : 'Approvals required',
      });
    } catch (err: any) {
      Toast.show({
        type: 'error',
        text1: 'Could not update',
        text2: typeof err === 'string' ? err : 'Please try again.',
      });
    }
  };

  const handleSaveName = async () => {
    if (!isOwner || !detail) return;
    const trimmed = editedName.trim();
    if (trimmed.length === 0 || trimmed === detail.name) return;
    setSavingName(true);
    try {
      await dispatch(patchGroup({ groupId, patch: { name: trimmed } })).unwrap();
      Toast.show({ type: 'success', text1: 'Saved' });
    } catch (err: any) {
      Toast.show({
        type: 'error',
        text1: 'Could not rename',
        text2: typeof err === 'string' ? err : 'Please try again.',
      });
    } finally {
      setSavingName(false);
    }
  };

  const handleArchive = () => {
    if (!isOwner || !detail) return;
    const isCurrentlyArchived = detail.archived_at != null;
    const title = isCurrentlyArchived ? 'Un-archive circle?' : 'Archive circle?';
    const message = isCurrentlyArchived
      ? 'Members regain full access. Invites, hand-offs, and item bindings will work again.'
      : '• No new invites or hand-offs\n• Items stay bound; nothing is deleted\n• Members keep view-only access\n• You can un-archive anytime';
    const confirmLabel = isCurrentlyArchived ? 'Un-archive' : 'Archive';

    const run = async () => {
      setArchiving(true);
      try {
        await dispatch(
          archiveGroup({
            groupId,
            archived: !isCurrentlyArchived,
          }),
        ).unwrap();
        Toast.show({
          type: 'success',
          text1: isCurrentlyArchived ? 'Circle un-archived' : 'Circle archived',
        });
        // Only bounce back on archive — un-archive stays on the
        // page so the user sees the controls re-enable.
        if (!isCurrentlyArchived) goBackOrHome(navigation);
        else dispatch(loadGroupDetail(groupId));
      } catch (err: any) {
        Toast.show({
          type: 'error',
          text1: isCurrentlyArchived ? 'Could not un-archive' : 'Could not archive',
          text2: typeof err === 'string' ? err : 'Please try again.',
        });
      } finally {
        setArchiving(false);
      }
    };

    // FE#242 — react-native-web's `Alert.alert` is a no-op (`static alert() {}`),
    // so on web the confirm (and therefore the archive) never fired. Mirror the
    // item-delete pattern (ItemDetailScreen.handleDelete): branch to the native
    // browser confirm on web; keep RN Alert for the native client.
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      if (window.confirm(`${title}\n\n${message}`)) run();
    } else {
      Alert.alert(title, message, [
        { text: 'Cancel', style: 'cancel' },
        {
          text: confirmLabel,
          style: isCurrentlyArchived ? 'default' : 'destructive',
          onPress: () => run(),
        },
      ]);
    }
  };

  // ── Render ──────────────────────────────────────────────────────
  const activeMembers = (detail?.members ?? []).filter(
    (m) => m.status === 'ACTIVE',
  );
  const memberCount = activeMembers.length;
  const onlyOwner = memberCount === 1 && isOwner;
  const nameDirty =
    detail != null && editedName.trim() !== detail.name && editedName.trim().length > 0;

  return (
    <View style={styles.page}>
      <Header
        onBack={() => goBackOrHome(navigation)}
        title={detail?.name ?? '…'}
        right={
          isOwner ? (
            <Pressable
              hitSlop={8}
              onPress={() => navigation.navigate('SharingRequests')}
              accessibilityRole="button"
              accessibilityLabel="More options"
              style={styles.menuBtn}
            >
              <MaterialIcons
                name="more-vert"
                size={22}
                color={fpColors.onSurface}
              />
            </Pressable>
          ) : null
        }
      />

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={{ flex: 1 }}
      >
        <ScrollView
          contentContainerStyle={styles.scroll}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={false}
              onRefresh={() => dispatch(loadGroupDetail(groupId))}
              tintColor={fpColors.primary}
            />
          }
        >
          {/* FE-208 — Initial loading.
              Previously this branch was gated on `loading.list` being
              true. On fast tap-through (user taps a crew card → nav
              pushes GroupDetail → component mounts BEFORE the
              loadGroupDetail dispatch in the mount effect fires) both
              `detail` and `loading.list` were false simultaneously,
              leaving the screen with just chrome and a blank body.
              We now render the loading state whenever `detail` is
              missing AND there's no error to surface — the worst case
              is a flash of "Loading…" before real data arrives, which
              is the right default for a navigated-to screen. */}
          {!detail && !error && (
            <View style={styles.loadingWrap}>
              <ActivityIndicator color={fpColors.primary} />
              <Text style={styles.loadingText}>Loading…</Text>
            </View>
          )}

          {/* Error / 403 */}
          {!detail && error && (
            <View style={styles.errorBanner}>
              <MaterialIcons
                name="error-outline"
                size={20}
                color={fpColors.error}
              />
              <View style={{ flex: 1 }}>
                <Text style={styles.errorTitle}>This circle isn't available</Text>
                <Text style={styles.errorMeta}>{error}</Text>
              </View>
              <Pressable
                onPress={() => goBackOrHome(navigation)}
                hitSlop={6}
                style={styles.retryBtn}
              >
                <Text style={styles.retryText}>Back</Text>
              </Pressable>
            </View>
          )}

          {detail && (
            <>
              {/* FE-219 — Archived banner. Sits above the header summary
                  so the read-only state is the FIRST thing the viewer
                  sees. Non-owners stay on the page so they can still
                  read past activity, but every mutation surface (invite
                  / add-items / handoff-request / borrow) returns 410
                  group_archived from the BE. */}
              {detail.archived_at && (
                <View
                  style={styles.archivedBanner}
                  testID="group-archived-banner"
                >
                  <MaterialIcons
                    name="archive"
                    size={18}
                    color={fpColors.onTertiaryContainer}
                  />
                  <Text style={styles.archivedBannerText} numberOfLines={2}>
                    This circle is archived. Read-only — new invites and
                    hand-offs are blocked.
                  </Text>
                </View>
              )}

              {/* Header summary */}
              <View style={styles.headerBlock}>
                <View style={styles.kindRow}>
                  <View style={styles.kindChip}>
                    <Text style={styles.kindText}>{KIND_LABEL[detail.kind]}</Text>
                  </View>
                  <Text style={styles.metaText}>
                    {memberCount} {memberCount === 1 ? 'member' : 'members'}
                  </Text>
                </View>
                {!!detail.description && (
                  <GroupDescription text={detail.description} />
                )}
              </View>

              {/* Members section */}
              <Text style={styles.sectionLabel}>Members</Text>

              {isAdmin && (
                <View
                  style={[
                    styles.inviteCard,
                    onlyOwner && styles.inviteCardEmpty,
                  ]}
                >
                  {onlyOwner ? (
                    <>
                      <Text style={styles.inviteEmptyTitle}>
                        Invite your first circle member
                      </Text>
                      <Text style={styles.inviteEmptyMeta}>
                        Add someone by email to get started.
                      </Text>
                    </>
                  ) : (
                    <Text style={styles.inviteLabel}>Invite by email</Text>
                  )}
                  <View style={styles.inviteRow}>
                    <TextInput
                      style={[styles.input, { flex: 1 }]}
                      value={inviteEmail}
                      onChangeText={setInviteEmail}
                      placeholder="dylan@example.com"
                      placeholderTextColor={fpColors.outline}
                      autoCapitalize="none"
                      keyboardType="email-address"
                      onSubmitEditing={submitInvite}
                      returnKeyType="send"
                      accessibilityLabel="Invitee email"
                    />
                    <Pressable
                      onPress={submitInvite}
                      disabled={loading.inviting}
                      style={({ pressed }) => [
                        styles.inviteBtn,
                        loading.inviting && { opacity: 0.6 },
                        pressed && { opacity: 0.85 },
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel="Send invite"
                    >
                      {loading.inviting ? (
                        <ActivityIndicator
                          size="small"
                          color={fpColors.onPrimary}
                        />
                      ) : (
                        <Text style={styles.inviteBtnText}>Send</Text>
                      )}
                    </Pressable>
                  </View>
                </View>
              )}

              {/* Search-UX §3.6 — Member filter.  Renders above the
                  member list when there are 4+ members (below that
                  the list scans visually faster than typing).  The
                  filter is case-insensitive over name + email +
                  invitee_email so it covers both ACTIVE members and
                  INVITED rows that show as email-only. */}
              {(detail.members ?? []).length >= 4 && (
                <View style={styles.memberFilterRow}>
                  <MaterialIcons
                    name="search"
                    size={16}
                    color={fpColors.outline}
                    style={{ marginRight: 6 }}
                  />
                  <TextInput
                    value={memberFilter}
                    onChangeText={setMemberFilter}
                    placeholder="Filter members"
                    placeholderTextColor={fpColors.outline}
                    style={styles.memberFilterInput}
                    autoCorrect={false}
                    autoCapitalize="none"
                    accessibilityLabel="Filter members"
                    testID="group-member-filter"
                  />
                  {memberFilter.length > 0 && (
                    <Pressable
                      onPress={() => setMemberFilter('')}
                      hitSlop={6}
                      accessibilityRole="button"
                      accessibilityLabel="Clear member filter"
                      testID="group-member-filter-clear"
                    >
                      <MaterialIcons
                        name="close"
                        size={16}
                        color={fpColors.outline}
                      />
                    </Pressable>
                  )}
                </View>
              )}

              <View style={styles.memberList}>
                {(detail.members ?? [])
                  .filter((m) => {
                    const q = memberFilter.trim().toLowerCase();
                    if (!q) return true;
                    const name = (m.name ?? '').toLowerCase();
                    const email = (m.email ?? '').toLowerCase();
                    const inviteeEmail = (
                      (m as any).invitee_email ?? ''
                    ).toLowerCase();
                    return (
                      name.includes(q) ||
                      email.includes(q) ||
                      inviteeEmail.includes(q)
                    );
                  })
                  .map((m) => {
                  const isMe =
                    m.user_id != null && m.user_id === currentUser?.id;
                  const canRemove =
                    isAdmin && m.role !== 'OWNER' && m.user_id != null;
                  const pendingEmail = m.invitee_email || m.email || '';
                  return (
                    <MemberRow
                      key={`${m.id ?? m.user_id ?? m.invitee_email ?? 'pending'}`}
                      name={m.name ?? undefined}
                      email={m.email || m.invitee_email || ''}
                      role={m.role}
                      userId={m.user_id ?? null}
                      isSelf={isMe}
                      callerIsOwner={isOwner}
                      callerIsAdmin={isAdmin}
                      pending={m.status === 'INVITED'}
                      onRemove={canRemove ? () => handleRemove(m) : undefined}
                      // FE-216 — cancel pending invite by email
                      onCancelInvite={
                        m.status === 'INVITED' && isAdmin && pendingEmail
                          ? () =>
                              handleCancelInvite(detail.id, pendingEmail)
                          : undefined
                      }
                      // FE-216 — resend invite (BE rotates token + re-emails)
                      onResendInvite={
                        m.status === 'INVITED' && isAdmin && pendingEmail
                          ? () =>
                              handleResendInvite(detail.id, pendingEmail)
                          : undefined
                      }
                      // FE-217 — role-change (atomic OWNER swap server-side)
                      onChangeRole={
                        !isMe && m.user_id != null && isAdmin
                          ? (next) =>
                              handleChangeRole(detail.id, m.user_id!, next)
                          : undefined
                      }
                    />
                  );
                })}
              </View>

              {/* FE-248 v2 — Pending borrow-request UI now lives
                  inline under each affected inventory row (managed
                  by GroupInventorySection). The standalone section
                  was removed because it disconnected the request
                  from its item; the inline strip pattern co-locates
                  them and scales better when many items have pending
                  requests at once (a sticky summary banner takes over
                  triage at >5 items). */}

              {/* Inventory section (Sprint 2 / FE-9.3) — visible to
                  every group member; admin-only actions are gated
                  inside the component. */}
              <GroupInventorySection
                groupId={detail.id}
                groupName={detail.name}
                isAdmin={isAdmin}
                /* PR31 — any ACTIVE member of the group can add
                   items to the shared inventory. Destructive
                   operations (unbind, remove a member) stay
                   gated to isAdmin inside the component. */
                canAddItems={myRole != null}
              />

              {/* Activity section (Sprint 4 / FE-10.6) — paginated
                  custody-event timeline. Visible to every member. */}
              <GroupActivityTab groupId={detail.id} />

              {/* Settings section — OWNER only */}
              {isOwner && (
                <>
                  <Text style={styles.sectionLabel}>Settings</Text>

                  <View style={styles.settingCard}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.settingTitle}>
                        Auto-approve hand-offs
                      </Text>
                      <Text style={styles.settingBody}>
                        Circle members can take items without explicit approval.
                        Off by default.
                      </Text>
                    </View>
                    <Switch
                      value={detail.auto_approve_handoff}
                      onValueChange={handleToggleAutoApprove}
                      disabled={loading.updating}
                      trackColor={{
                        false: fpColors.outlineVariant,
                        true: fpColors.primary,
                      }}
                      accessibilityLabel="Auto-approve hand-offs"
                    />
                  </View>

                  <View style={[styles.settingCard, styles.settingCardStack]}>
                    <Text style={styles.settingTitle}>Rename</Text>
                    <View style={styles.inviteRow}>
                      <TextInput
                        style={[styles.input, { flex: 1 }]}
                        value={editedName}
                        onChangeText={setEditedName}
                        placeholder={detail.name}
                        placeholderTextColor={fpColors.outline}
                        maxLength={120}
                        accessibilityLabel="Circle name"
                      />
                      <Pressable
                        onPress={handleSaveName}
                        disabled={!nameDirty || savingName}
                        style={({ pressed }) => [
                          styles.inviteBtn,
                          (!nameDirty || savingName) && { opacity: 0.45 },
                          pressed && nameDirty && { opacity: 0.85 },
                        ]}
                        accessibilityRole="button"
                      >
                        {savingName ? (
                          <ActivityIndicator
                            size="small"
                            color={fpColors.onPrimary}
                          />
                        ) : (
                          <Text style={styles.inviteBtnText}>Save</Text>
                        )}
                      </Pressable>
                    </View>
                  </View>

                  <Pressable
                    onPress={handleArchive}
                    disabled={archiving}
                    style={({ pressed }) => [
                      styles.archiveBtn,
                      archiving && { opacity: 0.6 },
                      pressed && { opacity: 0.85 },
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel={
                      detail.archived_at
                        ? `Un-archive ${detail.name}`
                        : `Archive ${detail.name}`
                    }
                    testID="group-archive-btn"
                  >
                    <MaterialIcons
                      name={detail.archived_at ? 'unarchive' : 'archive'}
                      size={18}
                      color={
                        detail.archived_at ? fpColors.primary : fpColors.error
                      }
                    />
                    <Text
                      style={[
                        styles.archiveText,
                        detail.archived_at ? { color: fpColors.primary } : null,
                      ]}
                    >
                      {detail.archived_at ? 'Un-archive circle' : 'Archive circle'}
                    </Text>
                  </Pressable>
                </>
              )}

              {/* Read-only setting hint for non-owners */}
              {!isOwner && detail.auto_approve_handoff && (
                <View style={styles.readOnlyHint}>
                  <MaterialIcons
                    name="info-outline"
                    size={16}
                    color={fpColors.tertiary}
                  />
                  <Text style={styles.readOnlyHintText}>
                    Auto-approve is on — you can take items from this circle
                    without an explicit ask.
                  </Text>
                </View>
              )}
            </>
          )}
        </ScrollView>
      </KeyboardAvoidingView>

      {/* FE-232 — 429 invite_cooldown live-countdown modal. Mounted at
          the page level so it overlays everything (form + member list)
          and auto-closes when the timer ticks to zero. */}
      <InviteCooldownModal
        visible={cooldown.visible}
        email={cooldown.email}
        expiresAt={cooldown.expiresAt}
        declineCount={cooldown.declineCount}
        onClose={() =>
          setCooldown({
            visible: false,
            email: null,
            expiresAt: null,
            declineCount: null,
          })
        }
      />
    </View>
  );
};

/* ────────────────────────────────────────────────────────────────────
 * Group description — Gap #108 (2026-05-27 audit)
 *
 * Used to be a plain unbounded <Text>, which let the E2E group's dev
 * note ("Baseline group seeded by e2e/scripts/seed-baseline-data.ts
 * (FE-258.2). Do not delete.") push the members list far below the
 * fold. Real users could paste arbitrarily long descriptions with the
 * same effect. Clamp to 2 lines + inline Read more / Read less.
 *
 * Detection uses `onTextLayout` — we render once unclamped to a hidden
 * shadow Text, count the lines, then show the toggle only when text
 * overflows. Avoids a flicker since both paths read the same string.
 * ──────────────────────────────────────────────────────────────────── */

const GroupDescription: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  return (
    <View>
      <Text
        style={styles.description}
        numberOfLines={expanded ? undefined : 2}
        onTextLayout={(e) => {
          // First layout pass — measure lines without affecting state.
          // We only need to know whether it would have been >2 lines if
          // unclamped, so check the lines array even when clamped.
          const lines = e?.nativeEvent?.lines?.length ?? 0;
          if (!expanded && lines >= 2) {
            // When clamped to 2 lines, RN-Web fills lines[1] with as
            // much text as fits; if the source text is longer than what
            // fits in 2 lines, the toggle is warranted. Use a length
            // heuristic since lineHeight measurement is unreliable.
            const renderedChars = e.nativeEvent.lines.reduce(
              (acc, l) => acc + (l.text?.length ?? 0),
              0,
            );
            if (renderedChars < text.length) setOverflows(true);
          }
        }}
      >
        {text}
      </Text>
      {overflows && (
        <Pressable
          onPress={() => setExpanded((v) => !v)}
          hitSlop={6}
          accessibilityRole="button"
          accessibilityLabel={expanded ? 'Show less' : 'Show full description'}
        >
          <Text style={styles.descriptionToggle}>
            {expanded ? 'Read less' : 'Read more'}
          </Text>
        </Pressable>
      )}
    </View>
  );
};

/* ────────────────────────────────────────────────────────────────────
 * Header sub-component
 * ──────────────────────────────────────────────────────────────────── */

const Header: React.FC<{
  onBack: () => void;
  title: string;
  right?: React.ReactNode;
}> = ({ onBack, title, right }) => (
  <View style={styles.topBar}>
    <View style={styles.topBarInner}>
      <Pressable
        onPress={onBack}
        hitSlop={8}
        accessibilityRole="button"
        accessibilityLabel="Back"
      >
        <MaterialIcons name="arrow-back" size={22} color={fpColors.onSurface} />
      </Pressable>
      <View style={styles.topBarCenter}>
        <BrandLogo variant="horizontal" height={20} tone="color" />
      </View>
      <View style={styles.topBarRight}>{right ?? <View style={{ width: 22 }} />}</View>
    </View>
    {title.length > 0 && (
      <Text
        style={styles.title}
        numberOfLines={1}
        accessibilityRole="header"
      >
        {title}
      </Text>
    )}
  </View>
);

/* ────────────────────────────────────────────────────────────────────
 * Styles
 * ──────────────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },

  topBar: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
    paddingBottom: 12,
  },
  topBarInner: {
    height: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
  },
  topBarCenter: { flex: 1, alignItems: 'center' },
  topBarRight: { width: 44, alignItems: 'flex-end' },
  menuBtn: { padding: 6 },
  title: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 22,
    letterSpacing: -0.4,
    color: fpColors.onSurface,
    paddingHorizontal: 16,
    marginTop: 6,
  },

  scroll: { padding: 16, paddingBottom: 32, gap: 14 },

  loadingWrap: { paddingVertical: 48, alignItems: 'center', gap: 10 },
  loadingText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },

  errorWrap: {
    flex: 1,
    paddingVertical: 64,
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 32,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderRadius: 14,
    backgroundColor: `${fpColors.errorContainer}40`,
    borderWidth: 1,
    borderColor: fpColors.errorContainer,
  },
  errorTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.error,
  },
  errorMeta: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onErrorContainer,
    marginTop: 2,
    textAlign: 'center',
  },
  retryBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: fpColors.error,
  },
  retryText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.onPrimary,
  },
  primaryCta: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: fpColors.primary,
    marginTop: 12,
  },
  primaryCtaText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onPrimary,
  },

  headerBlock: { gap: 8, marginBottom: 4 },
  kindRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  kindChip: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: `${fpColors.primary}14`,
  },
  kindText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    letterSpacing: 0.4,
    color: fpColors.primary,
  },
  metaText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  description: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
    color: fpColors.onSurfaceVariant,
  },
  descriptionToggle: {
    marginTop: 4,
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.primary,
  },

  sectionLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    letterSpacing: 0.6,
    color: fpColors.onSurfaceVariant,
    textTransform: 'uppercase',
    marginTop: 6,
  },

  inviteCard: {
    padding: 14,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    gap: 10,
  },
  inviteCardEmpty: {
    backgroundColor: `${fpColors.primary}10`,
    borderColor: `${fpColors.primary}33`,
  },
  inviteEmptyTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.onSurface,
  },
  inviteEmptyMeta: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    lineHeight: 16,
  },
  inviteLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  inviteRow: { flexDirection: 'row', gap: 8, alignItems: 'center' },
  input: {
    // minWidth:0 lets a `flex:1` input shrink below its text's intrinsic
    // width on react-native-web; without it the input refuses to shrink and
    // pushes an adjacent button (Save / Send) off the right edge (FE#242 follow-up).
    minWidth: 0,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: fpColors.onSurface,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  inviteBtn: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: fpColors.primary,
    minWidth: 70,
    alignItems: 'center',
    justifyContent: 'center',
  },
  inviteBtnText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onPrimary,
  },

  memberList: { gap: 10, marginTop: 4 },
  // Search-UX §3.6 — member filter chrome.  Same rounded-border
  // pattern the GroupInventorySection search input uses so the two
  // search inputs read as one consistent affordance across the
  // screen.
  memberFilterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    marginBottom: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  memberFilterInput: {
    flex: 1,
    fontSize: 14,
    color: fpColors.onSurface,
    padding: 0,
    margin: 0,
  },

  settingCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 14,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  // Rename card stacks vertically (label above a full-width field+Save row) so
  // the Save button never gets pushed off-screen on a 390px viewport. The
  // default settingCard is a row (used by the auto-approve toggle).
  settingCardStack: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 10,
  },
  settingTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  settingBody: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    lineHeight: 16,
    color: fpColors.onSurfaceVariant,
    marginTop: 4,
  },

  archiveBtn: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.errorContainer,
    backgroundColor: `${fpColors.errorContainer}26`,
  },
  archiveText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.error,
  },
  // FE-219 — Archived banner at the top of the detail page.
  archivedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: fpColors.tertiaryContainer,
    marginBottom: 12,
  },
  archivedBannerText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onTertiaryContainer,
    lineHeight: 16,
  },

  readOnlyHint: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    padding: 12,
    borderRadius: 10,
    backgroundColor: `${fpColors.tertiaryContainer}26`,
  },
  readOnlyHintText: {
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    lineHeight: 16,
    color: fpColors.onSurface,
  },
});
